import { supabase } from '@/lib/supabase';

export interface CompleteDeletionResult {
  success: boolean;
  message: string;
  error?: string;
  deletedUser?: {
    id: string;
    email: string;
    name: string;
    role: string;
  };
}

/**
 * Completely deletes a user from all tables including authentication
 * 
 * NOTE: This function will show CORS/404 errors in the console if the Edge Function
 * is not deployed. These errors are HARMLESS and expected. The function will automatically
 * fall back to application-level deletion (deletes from all app tables except auth.users).
 * 
 * To remove console errors, either:
 * 1. Deploy the Edge Function: `supabase functions deploy delete-user`
 * 2. Or set this environment variable: VITE_SKIP_EDGE_FUNCTION_DELETION=true
 */
export async function removeUserCompletely(userId: string): Promise<CompleteDeletionResult> {
  try {
    console.log('Starting complete user deletion for:', userId);
    
    // Get the current user's session token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session found');
    }

    // Check if Edge Function deletion should be skipped
    // Set VITE_SKIP_EDGE_FUNCTION_DELETION=true to avoid CORS console errors
    const skipEdgeFunction = import.meta.env.VITE_SKIP_EDGE_FUNCTION_DELETION === 'true';

    // Try to call the Supabase Edge Function (if deployed)
    // NOTE: This will show CORS errors in console if function doesn't exist
    // These errors are harmless - the function will fall back to app-level deletion
    if (!skipEdgeFunction) {
      try {
        const { data, error } = await supabase.functions.invoke('delete-user', {
          body: { userId },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        // If function exists and works, return the result
        if (!error && data?.success) {
          console.log('Complete deletion successful via Edge Function');
          return data;
        }

        // If we get an error (CORS, 404, etc.), silently fall through to fallback
        // These errors are expected when Edge Function is not deployed
      } catch (invokeError: any) {
        // CORS errors, network errors, or function not found
        // These are all expected when Edge Function is not deployed
        // Silently catch and continue with fallback deletion
      }
    }

    // FALLBACK: Delete from all application tables
    // This permanently removes the user from all app tables (but not auth.users)
    console.log('Deleting user from all application tables...');
    
    // Get user profile and hospital_id before deletion
    const { data: profile, error: profileFetchError } = await supabase
      .from('profiles')
      .select('email, name, role, hospital_id')
      .eq('id', userId)
      .single();

    if (profileFetchError) {
      throw new Error(`Failed to fetch user profile: ${profileFetchError.message}`);
    }

    if (!profile) {
      throw new Error('User profile not found');
    }

    const hospitalId = profile.hospital_id;
    const userEmail = profile.email || 'Unknown';
    const userName = profile.name || 'Unknown';
    const userRole = profile.role || 'Unknown';

    // ============================================
    // DELETE FROM ALL APPLICATION TABLES
    // ============================================
    console.log('Deleting user from all application tables...');

    // 1. Delete prescriptions created by this doctor (if doctor)
    if (userRole === 'doctor') {
      let prescriptionsQuery = supabase
        .from('prescriptions')
        .delete()
        .eq('doctor_id', userId);
      
      if (hospitalId) {
        prescriptionsQuery = prescriptionsQuery.eq('hospital_id', hospitalId);
      }
      
      const { error: prescriptionsError } = await prescriptionsQuery;
      if (prescriptionsError) {
        console.warn('Could not delete prescriptions:', prescriptionsError);
      } else {
        console.log('Deleted prescriptions for doctor');
      }
    }

    // 2. Delete from doctors table (if doctor)
    if (userRole === 'doctor') {
      let doctorsQuery = supabase
        .from('doctors')
        .delete()
        .eq('id', userId);
      
      if (hospitalId) {
        doctorsQuery = doctorsQuery.eq('hospital_id', hospitalId);
      }
      
      const { error: doctorsError } = await doctorsQuery;
      if (doctorsError) {
        console.warn('Could not delete from doctors table:', doctorsError);
      } else {
        console.log('Deleted from doctors table');
      }
    }

    // 3. Delete session logs (if table exists - optional/non-critical)
    // Note: Table name is 'user_session_logs', not 'session_logs'
    // This deletion is non-critical and won't block user deletion if it fails
    try {
      const { error: sessionLogsError } = await supabase
        .from('user_session_logs')
        .delete()
        .eq('user_id', userId);
      
      if (sessionLogsError) {
        // 404/PGRST301 means table doesn't exist - this is fine, just skip
        if (sessionLogsError.code === '42P01' || 
            sessionLogsError.code === 'PGRST301' ||
            sessionLogsError.code === 'PGRST116' ||
            sessionLogsError.message?.includes('404') || 
            sessionLogsError.message?.includes('does not exist') ||
            sessionLogsError.message?.includes('Not Found')) {
          // Table doesn't exist - silently skip (this is fine)
        } else {
          // Other error - log but don't block
          console.warn('Could not delete session logs (non-critical):', sessionLogsError.message);
        }
      }
      // Success or skip - either way, continue with deletion
    } catch (error: any) {
      // Non-critical - any error here is ignored
      // Session logs deletion is optional
    }

    // 4. Delete from profiles (last, as it might be referenced)
    let profilesQuery = supabase
      .from('profiles')
      .delete()
      .eq('id', userId);
    
    if (hospitalId) {
      profilesQuery = profilesQuery.eq('hospital_id', hospitalId);
    }
    
    const { data: deleteData, error: profilesError } = await profilesQuery.select();
    
    if (profilesError) {
      console.error('Profile deletion error:', profilesError);
      throw new Error(`Failed to delete user profile: ${profilesError.message}. Code: ${profilesError.code}`);
    }
    
    // Verify deletion was successful
    const { data: verifyProfile, error: verifyError } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .maybeSingle();
    
    if (verifyError && verifyError.code !== 'PGRST116') {
      console.warn('Could not verify profile deletion:', verifyError);
    } else if (verifyProfile) {
      console.error('⚠️ Profile still exists after deletion! User ID:', userId);
      throw new Error('Profile deletion failed - user still exists in database. This may be due to RLS policies or trigger recreating the profile.');
    }
    
    console.log('✅ Verified: Profile successfully deleted from database');

    // ============================================
    // NOTE: auth.users deletion requires Edge Function
    // ============================================
    console.warn('⚠️ User deleted from all application tables.');
    console.warn('⚠️ To delete from auth.users, deploy the Edge Function or run SQL manually.');
    console.warn(`⚠️ See: database/permanently_delete_user.sql for manual deletion from auth.users`);

    return {
      success: true,
      message: 'User permanently deleted from all application tables. To complete deletion, delete from auth.users using the Edge Function or run the SQL script: database/permanently_delete_user.sql',
      deletedUser: {
        id: userId,
        email: userEmail,
        name: userName,
        role: userRole
      }
    };

  } catch (error) {
    console.error('Complete deletion error:', error);
    return {
      success: false,
      error: (error as Error).message,
      message: 'Failed to delete user. Please try deactivation instead.'
    };
  }
}

/**
 * Alternative method using the database function (marks as deleted)
 * This doesn't actually delete from auth.users but marks the user as deleted
 */
export async function markUserAsDeleted(userId: string): Promise<CompleteDeletionResult> {
  try {
    console.log('Marking user as deleted:', userId);
    
    const { data, error } = await supabase.rpc('delete_user_completely', {
      user_id: userId
    });

    if (error) {
      console.error('Database function error:', error);
      throw new Error(`Failed to mark user as deleted: ${error.message}`);
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to mark user as deleted');
    }

    console.log('User marked as deleted:', data);
    return {
      success: true,
      message: 'User marked as deleted. Auth deletion requires server-side action.',
      deletedUser: {
        id: userId,
        email: 'Unknown',
        name: 'Unknown',
        role: 'Unknown'
      }
    };

  } catch (error) {
    console.error('Mark as deleted error:', error);
    return {
      success: false,
      error: (error as Error).message,
      message: 'Failed to mark user as deleted'
    };
  }
}
